import React from "react";
import { DateTime } from "luxon";
import {
  Box,
  Typography,
  MenuItem,
  Chip,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Stack,
  Divider,
} from "@mui/material";
import {
  AccessTime,
  Speed,
  Schedule,
  Business,
  LocationOn,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useRequestCountdowns } from "../../../hooks/useRequestCountdowns";

const NotaryNotifications = ({ onNotificationClick, notificationLoading }) => {
  const realTimeRequests = useSelector(
    (state) => state.notarySession?.notaryRequests || []
  );
  const { formatTimeRemaining, getTimeRemaining, isExpired } =
    useRequestCountdowns();

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const utcDate = DateTime.fromISO(dateString, { zone: "utc" });
    const localDate = utcDate.setZone("America/New_York"); // Default timezone
    return localDate.toFormat("MMM dd, hh:mm a");
  };

  const getRequestTypeInfo = (type) => {
    const isImmediate = type === "immediate";
    return {
      type: isImmediate ? "Expedited Request" : "Later Date Request",
      color: isImmediate ? "error" : "warning",
      icon: isImmediate ? <Speed /> : <Schedule />,
    };
  };

  if (!realTimeRequests || realTimeRequests.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="body2" color="textSecondary">
          No live notarization requests
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        maxHeight: "400px",
        overflowY: "auto",
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
          borderRadius: "3px",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#c1c1c1",
          borderRadius: "3px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "#a8a8a8",
        },
      }}
    >
      {realTimeRequests.map((request, index) => {
        const requestTypeInfo = getRequestTypeInfo(
          request.instant ? "immediate" : "later"
        );
        const timeRemainingSeconds = getTimeRemaining(request.id);
        const timeRemaining = formatTimeRemaining(timeRemainingSeconds);
        const requestExpired = isExpired(request.id);
        const isLoading = notificationLoading === request.id;

        return (
          <MenuItem
            key={index}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              p: 2,
              minHeight: "auto",
              borderBottom:
                index < realTimeRequests.length - 1
                  ? "1px solid #e0e0e0"
                  : "none",
              backgroundColor: requestExpired ? "#f8f9fa" : "inherit",
              cursor: isLoading ? "default" : "pointer",
              "&:hover": {
                backgroundColor: isLoading ? "inherit" : "#f0f7ff",
              },
              "&:last-child": {
                borderBottom: "none",
              },
            }}
            disableRipple={true}
            onClick={() => {
              if (!isLoading) {
                onNotificationClick("realtime");
              }
            }}
          >
            {isLoading && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0, 0, 0, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 10,
                  pointerEvents: "none",
                }}
              >
                <CircularProgress size={25} />
              </Box>
            )}

            {/* Header with request type and time remaining */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1.5}
            >
              <Box display="flex" alignItems="center" gap={1}>
                {requestTypeInfo.icon}
                <Typography variant="h6" fontWeight="bold" color="primary">
                  {requestTypeInfo.type}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <AccessTime
                  fontSize="small"
                  color={requestExpired ? "error" : "warning"}
                />
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color={requestExpired ? "error" : "warning"}
                >
                  {timeRemaining}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 1.5 }} />

            {/* Request details */}
            <Stack spacing={1.5}>
              {/* Company/Requester */}
              <Box display="flex" alignItems="center" gap={1}>
                <Business fontSize="small" color="action" />
                <Typography variant="body2" color="textSecondary">
                  <strong>Company:</strong> {request.requestor || "N/A"}
                </Typography>
              </Box>

              {/* Request date */}
              <Box display="flex" alignItems="center" gap={1}>
                <AccessTime fontSize="small" color="action" />
                <Typography variant="body2" color="textSecondary">
                  <strong>Requested:</strong> {request.date || "N/A"}
                </Typography>
              </Box>

              {/* Action hint */}
              <Box sx={{ mt: 1, pt: 1, borderTop: "1px solid #f0f0f0" }}>
                <Typography
                  variant="caption"
                  color="primary"
                  sx={{
                    fontStyle: "italic",
                    fontWeight: "medium",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  👆 Click to view details and manage request
                </Typography>
              </Box>
            </Stack>
          </MenuItem>
        );
      })}
    </Box>
  );
};

export default NotaryNotifications;
